import React, { useContext, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import MUIButton from "@mui/material/Button";
import MUIDeleteIcon from "@mui/icons-material/Delete";
import MUISaveButton from "@mui/icons-material/Save";
import MUILoadingButton from "@mui/lab/LoadingButton";
import MUIMenuItem from "@mui/material/MenuItem";
import MUIListItemIcon from "@mui/material/ListItemIcon";
import { enqueueSnackbar as NotistackEnqueueSnackbar } from "notistack";

import LayerFunctional from "../../../LayerFunctional";
import MainContext from "../../../Context/MainContext/MainContext";
import CardAdminDashboardGeneral from "../../../Component/Card/Admin/Dashboard/CardAdminDashboardGeneral";
import CardAdminDashboardWidgets from "../../../Component/Card/Admin/Dashboard/CardAdminDashboardWidgets";
import PageHeader from "../../../Component/PageHeader";
import Menu from "../../../Component/Menu";
import DialogConfirm from "../../../Component/Dialog/DialogConfirm";
import Validator from "../../../Utility/Validator";
import Dashboard from "../../../Utility/Crud/Dashboard";
import api from "../../../Seating/Security/api";

export default function AdminDashboardCreateEdit() {
  const context = useContext(MainContext);
  const history = useHistory();
  const { id } = useParams();
  const isUpdate = Boolean(id);
  const dashboardId = Number(id);
  const initialDashboardWidgets = isUpdate
    ? Object.values(context.dashboardWidgetsIndexed)
        .filter(
          (dashboardWidget) => dashboardWidget.dashboardId === dashboardId,
        )
        .map((dashboardWidget) => ({
          ...dashboardWidget,
          name: dashboardWidget.name ?? "",
          jsonConfiguration: dashboardWidget.jsonConfiguration ?? "",
        }))
    : [];

  const [dashboard, setDashboard] = useState(() =>
    isUpdate ? context.dashboardsIndexed[dashboardId] : {},
  );
  const [dashboardWidgets, setDashboardWidgets] = useState(
    initialDashboardWidgets,
  );
  const [dashboardWidgetsOriginal] = useState(initialDashboardWidgets);
  const [isProcessing, setIsProcessing] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [validationIssues, setValidationIssues] = useState({
    dashboardWidgets: {},
  });

  const createUpdateDeleteDashboardWidgets = async (dashboardId) => {
    const currentRows = dashboardWidgets.map((row, index) => ({
      ...row,
      dashboardId: dashboardId,
      ordinal: index + 1,
    }));
    const rowsToCreate = currentRows.filter((row) => row.id <= 0);
    const rowsToUpdate = currentRows.filter((row) => row.id > 0);
    const rowsToDelete = dashboardWidgetsOriginal.filter((originalRow) => {
      return currentRows.find((row) => row.id === originalRow.id) === undefined;
    });

    const promises = [];

    rowsToCreate.forEach((row) => {
      const payload = {
        dashboardId: row.dashboardId,
        widgetId: row.widgetId,
        name: row.name.trim(),
        ordinal: row.ordinal,
        jsonConfiguration: row.jsonConfiguration,
      };
      promises.push(
        api.send("POST", "/dashboardWidget", payload).then((createdRow) => {
          context.updateIndexed("dashboardWidgetsIndexed", createdRow);
        }),
      );
    });

    rowsToUpdate.forEach((row) => {
      const payload = {
        ...row,
        name: row.name.trim(),
        jsonConfiguration: row.jsonConfiguration,
      };
      promises.push(
        api.send("PUT", "/dashboardWidget", payload).then((updatedRow) => {
          context.updateIndexed("dashboardWidgetsIndexed", updatedRow);
        }),
      );
    });

    rowsToDelete.forEach((row) => {
      promises.push(
        api.send("DELETE", `/dashboardWidget/${row.id}`).then(() => {
          context.deleteIndexed("dashboardWidgetsIndexed", row.id);
        }),
      );
    });

    return Promise.all(promises);
  };

  const createDashboard = async () => {
    setIsProcessing(true);

    return api
      .send("POST", "/advancedDashboard", dashboard)
      .then((createdDashboard) => {
        context.updateIndexed("dashboardsIndexed", createdDashboard);
        setDashboard(createdDashboard);
        return createUpdateDeleteDashboardWidgets(createdDashboard.id).then(
          () => createdDashboard.id,
        );
      })
      .then(() => {
        history.push("/admin/dashboards");
        NotistackEnqueueSnackbar("Dashboard created successfully!", {
          variant: "success",
        });
      })
      .catch((error) => {
        console.log(error);
        NotistackEnqueueSnackbar(
          "Oh no! There was an error creating a new Dashboard.",
          { variant: "error" },
        );
        setIsProcessing(false);
      });
  };

  const updateDashboard = async () => {
    setIsProcessing(true);

    return api
      .send("PUT", "/advancedDashboard", dashboard)
      .then((updatedDashboard) => {
        context.updateIndexed("dashboardsIndexed", updatedDashboard);
        return createUpdateDeleteDashboardWidgets(updatedDashboard.id);
      })
      .then(() => {
        history.goBack();
        NotistackEnqueueSnackbar("Dashboard updated successfully!", {
          variant: "success",
        });
      })
      .catch((error) => {
        console.log(error);
        NotistackEnqueueSnackbar(
          "Oh no! There was an error updating the Dashboard.",
          { variant: "error" },
        );
        setIsProcessing(false);
      });
  };

  const deleteDashboard = async () => {
    setIsProcessing(true);

    return api
      .send("DELETE", `/advancedDashboard/${dashboard.id}`)
      .then(() => {
        context.deleteIndexed("dashboardsIndexed", dashboard.id);
        history.goBack();
        NotistackEnqueueSnackbar("Dashboard deleted successfully!", {
          variant: "success",
        });
      })
      .catch((error) => {
        console.log(error);
        NotistackEnqueueSnackbar(
          "Oh no! There was an error deleting the Dashboard.",
          { variant: "error" },
        );
        setConfirmDialogOpen(false);
        setIsProcessing(false);
      });
  };

  return (
    <LayerFunctional
      getMaxWidth={() => 1000}
      renderHeader={() => {
        const title = isUpdate ? "Edit Dashboard" : "Create Dashboard";
        const subtitle =
          isUpdate && dashboard?.id
            ? `${Dashboard.getDisplayName(dashboard)} • ${dashboard.id}`
            : null;

        const handleClickSave = () => {
          const dashboardValidationIssues = Validator.validate(dashboard, {
            name: {
              required: true,
            },
          });

          const dashboardWidgetValidationIssues = {};
          dashboardWidgets.forEach((dashboardWidget) => {
            const widgetValidationIssues = Validator.validate(dashboardWidget, {
              name: {
                type: "custom",
                function: (value) => {
                  return value.trim() === "" ? "Required" : true;
                },
              },
              jsonConfiguration: {
                type: "custom",
                function: (value) => {
                  const jsonConfiguration = value.trim();
                  if (jsonConfiguration === "") {
                    return "Required";
                  }

                  try {
                    JSON.parse(jsonConfiguration);
                    return true;
                  } catch (_error) {
                    return "Must be valid JSON";
                  }
                },
              },
            });

            if (Object.keys(widgetValidationIssues).length > 0) {
              dashboardWidgetValidationIssues[dashboardWidget.id] =
                widgetValidationIssues;
            }
          });

          const nextValidationIssues = {
            ...dashboardValidationIssues,
            dashboardWidgets: dashboardWidgetValidationIssues,
          };

          setValidationIssues(nextValidationIssues);

          if (
            Object.keys(dashboardValidationIssues).length === 0 &&
            Object.keys(dashboardWidgetValidationIssues).length === 0
          ) {
            if (isUpdate) {
              updateDashboard();
            } else {
              createDashboard();
            }
          } else {
            NotistackEnqueueSnackbar("Please correct the displayed issues.", {
              variant: "error",
            });
          }
        };

        const buttons = [
          <MUIButton key="back" onClick={() => history.goBack()}>
            Back
          </MUIButton>,
          <MUILoadingButton
            key="save"
            loading={isProcessing}
            loadingPosition="start"
            onClick={handleClickSave}
            variant="contained"
            startIcon={<MUISaveButton />}
          >
            {isUpdate ? "Save" : "Create"}
          </MUILoadingButton>,
        ];

        if (isUpdate) {
          const menuItems = [
            <MUIMenuItem
              key="delete"
              onClick={() => setConfirmDialogOpen(true)}
            >
              <MUIListItemIcon>
                <MUIDeleteIcon fontSize="small" />
              </MUIListItemIcon>
              Delete
            </MUIMenuItem>,
          ];
          buttons.push(<Menu key="menu" menuItems={menuItems} />);
        }

        return (
          <PageHeader title={title} subtitle={subtitle} buttons={buttons} />
        );
      }}
      renderContent={() => (
        <>
          <CardAdminDashboardGeneral
            dashboard={dashboard}
            onChange={(changes) => {
              setDashboard((previousDashboard) => ({
                ...previousDashboard,
                ...changes,
              }));
            }}
            validationIssues={validationIssues}
          />
          <CardAdminDashboardWidgets
            dashboard={dashboard}
            dashboardWidgets={dashboardWidgets}
            onChange={(nextDashboardWidgets) =>
              setDashboardWidgets(nextDashboardWidgets)
            }
            validationIssues={validationIssues.dashboardWidgets}
          />
          <DialogConfirm
            open={confirmDialogOpen}
            onClose={() => setConfirmDialogOpen(false)}
            onConfirm={() => deleteDashboard()}
            text="Are you sure you want to delete this Dashboard? This cannot be undone."
            header="Delete Dashboard"
            loading={isProcessing}
          />
        </>
      )}
    />
  );
}
